import { Injectable, inject } from '@angular/core';
import { NotifyService } from '../../notify-service';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { KpiValueEntity, TrendDirectionEnum } from '@gishub/domain';
import { IntersectionCount } from '../services/community-intersection-service';
import { ExecutiveState } from '../states/executive-state';
import { UbigeoState } from '../states/ubigeo-state';
import {
  ArcGisQueryRepositoryProvider,
} from '../../shared/providers/repository-providers';

import { UbigeoProvider } from '../providers/ubigeo-provider';

interface DgtKpiDef {
  key: string;
  label: string;
  unit: string;
  url: string;
  hasProxy: boolean;
  badge: string;
  fields: { region: string; provincia: string; distrito: string };
  useGeometryFilter?: boolean;
  codeField?: string;
}

const DGT_KPIS: DgtKpiDef[] = [
  { key: 'comunidadesCampesinas', label: 'Comunidades campesinas', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/9', hasProxy: false, badge: 'CC', fields: { region: 'nom_dpto', provincia: 'nom_prov', distrito: 'nom_dist' }, useGeometryFilter: true },
  { key: 'comunidadesNativas', label: 'Comunidades nativas', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/10', hasProxy: false, badge: 'CN', fields: { region: 'nom_dpto', provincia: 'nom_prov', distrito: 'nom_dist' }, useGeometryFilter: true },
  { key: 'agencias', label: 'Agencias agrarias', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winlmprap14/rest/services/temporal2/OBSRV_INFOBASE/MapServer/8', hasProxy: false, badge: 'AG', fields: { region: 'NOMBDEP', provincia: 'NOMBPROV', distrito: 'NOMBDIST' }, useGeometryFilter: true },
  /*{ key: 'centrosPoblados', label: 'Centros poblados', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/32', hasProxy: false, badge: 'CCPP', fields: { region: 'nombdep', provincia: 'nombprov', distrito: 'nombdist' } },
  { key: 'proyectosInversion', label: 'Proyectos de inversión', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/7', hasProxy: false, badge: 'INVERSIÓN', fields: { region: 'DPTO', provincia: 'PROV', distrito: 'DIST' } },*/
  { key: 'espacios', label: 'Espacios de diálogo', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/4', hasProxy: false, badge: 'ED', fields: { region: 'region', provincia: 'provincia', distrito: 'distrito' }, useGeometryFilter: false },
  { key: 'compromisos', label: 'Compromisos agrarios', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/1', hasProxy: false, badge: 'CA', fields: { region: 'region', provincia: 'provincia', distrito: 'distrito' }, useGeometryFilter: false },
  { key: 'intervenciones', label: 'Intervenciones sectoriales', unit: '', url: 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/6', hasProxy: false, badge: 'IS', fields: { region: 'region', provincia: 'provincia', distrito: 'distrito' }, useGeometryFilter: false },
];

const initialExecutiveState: ExecutiveState = { kpis: [], tableConfig: null, loading: false };

@Injectable({ providedIn: 'root' })
export class ExecutiveNotifier extends BaseNotifier<ExecutiveState> {
  private get notify() { return inject(NotifyService); }
  private _lastGeometry: any = null;
  private _savedKpis: KpiValueEntity[] | null = null;

  constructor(ref: Ref) {
    super(ref, initialExecutiveState);

    this.ref.createEffect(() => {
      const u = this.ref.read(UbigeoProvider).state();
      const g = u.currentGeometry;
      // Cada cambio de provincia/distrito crea un nuevo objeto Polygon
      if (g && g !== this._lastGeometry) {
        this._lastGeometry = g;
        this.updateState(s => ({ ...s, loading: true }));
        this.refreshKpis(g).finally(() => {
          this._syncTableConfigWhere(this.ubigeo.state());
          this.updateState(s => ({ ...s, loading: false }));
        });
      }
    });
  }

  private get queryRepo() { return this.ref.read(ArcGisQueryRepositoryProvider).instance; }
  private get ubigeo() { return this.ref.read(UbigeoProvider); }

  override async build(): Promise<void> {
    const u = this.ubigeo.state();
    if (!u.currentGeometry) return;
    this._lastGeometry = u.currentGeometry;
    this.updateState(s => ({ ...s, loading: true }));
    try {
      await this.refreshKpis(u.currentGeometry);
    } finally {
      this.updateState(s => ({ ...s, loading: false }));
    }
  }

  async refreshKpis(geometry: any): Promise<void> {
    const u = this.ubigeo.state();
    const geo = typeof geometry.toJSON === 'function' ? geometry.toJSON() : geometry;
    const results = await Promise.allSettled(
      DGT_KPIS.map(k => {
        const useGeo = k.useGeometryFilter !== false && !k.codeField;
        const where = useGeo ? '1=1' : this.getWhereClause(k.key, u);
        return this.queryRepo.queryLayerCount(k.url, where, useGeo ? geo : undefined, 'esriGeometryPolygon', 'esriSpatialRelIntersects', k.hasProxy)
          .then(r => ({ key: k.key, count: r.data ?? 0 }));
      })
    );
    const kpis: KpiValueEntity[] = results.map((r, i) => {
      const d = DGT_KPIS[i];
      return { def: { key: d.key, label: d.label, unit: d.unit }, value: (r.status === 'fulfilled' ? r.value.count : 0).toLocaleString('es-PE'), trend: TrendDirectionEnum.Stable, badge: d.badge };
    });
    this.setState({ ...this.getStateValue(), kpis });
  }

  private getWhereClause(key: string, u: UbigeoState): string {
    const d = DGT_KPIS.find(x => x.key === key);
    if (!d || !u.selectedDepartmentName) return '1=1';
    const hp = u.selectedProvinceName && u.selectedProvinceName !== 'Toda la Región';
    const hd = u.selectedDistrictName && u.selectedDistrictName !== 'Todos los Distritos';
    if (d.codeField) {
      if (hd && u.selectedDistrict) return `${d.codeField} = '${u.selectedDistrict}'`;
      if (hp && u.selectedProvince) return `${d.codeField} LIKE '${u.selectedProvince}%'`;
      if (u.selectedDepartment) return `${d.codeField} LIKE '${u.selectedDepartment}%'`;
      return '1=1';
    }
    const f = hd ? d.fields.distrito : (hp ? d.fields.provincia : d.fields.region);
    const r = hd ? u.selectedDistrictName : (hp ? u.selectedProvinceName : u.selectedDepartmentName);
    const n = r.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    if (n === 'JUNIN') return `(${f} LIKE '%JUNIN%' OR ${f} LIKE '%JUNÍN%')`;
    if (n === 'HUANUCO') return `(${f} LIKE '%HUANUCO%' OR ${f} LIKE '%HUÁNUCO%')`;
    if (n === 'APURIMAC') return `(${f} LIKE '%APURIMAC%' OR ${f} LIKE '%APURÍMAC%')`;
    return `${f} LIKE '%${n}%'`;
  }

  openTable(key: string, extraWhere?: string): void {
    const d = DGT_KPIS.find(x => x.key === key);
    if (!d) return;
    const useGeo = d.useGeometryFilter !== false && !d.codeField;
    const baseWhere = useGeo ? '1=1' : this.getWhereClause(key, this.ubigeo.state());
    const where = extraWhere ? `(${baseWhere}) AND (${extraWhere})` : baseWhere;
    this.updateState(s => ({ ...s, tableConfig: { ...d, where, fields: [] } }));
  }

  refreshTableFilter(key: string, extraWhere?: string): void {
    const s = this.getStateValue();
    if (!s.tableConfig || s.tableConfig.key !== key) return;
    const d = DGT_KPIS.find(x => x.key === key);
    if (!d) return;
    const useGeo = d.useGeometryFilter !== false && !d.codeField;
    const baseWhere = useGeo ? '1=1' : this.getWhereClause(key, this.ubigeo.state());
    const where = extraWhere ? `(${baseWhere}) AND (${extraWhere})` : baseWhere;
    if (where !== s.tableConfig.where) {
      this.updateState(st => ({ ...st, tableConfig: { ...st.tableConfig!, where } }));
    }
  }

  private _syncTableConfigWhere(u: UbigeoState): void {
    const s = this.getStateValue();
    if (!s.tableConfig) return;
    const d = DGT_KPIS.find(x => x.key === s.tableConfig!.key);
    if (!d) return;
    const useGeo = d.useGeometryFilter !== false && !d.codeField;
    const newWhere = useGeo ? '1=1' : this.getWhereClause(d.key, u);
    if (newWhere !== s.tableConfig.where) {
      this.updateState(st => ({ ...st, tableConfig: { ...st.tableConfig!, where: newWhere } }));
    }
  }

  applyIntersectionOverride(counts: IntersectionCount[]): void {
    const s = this.getStateValue();
    if (!this._savedKpis) this._savedKpis = [...s.kpis];
    const updated = s.kpis.map(kpi => {
      const m = counts.find(c => c.key === kpi.def.key);
      return m ? { ...kpi, value: m.count.toLocaleString('es-PE') } : kpi;
    });
    this.updateState(st => ({ ...st, kpis: updated }));
  }

  clearIntersectionOverride(): void {
    if (this._savedKpis) {
      this.updateState(st => ({ ...st, kpis: this._savedKpis! }));
      this._savedKpis = null;
    }
  }

  closeTable(): void { this.updateState(s => ({ ...s, tableConfig: null })); }
}